import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { LogIn, LogOut, List, Edit, ArrowLeft } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  zone: {
    id: number
    name: string
    eventName: string
    eventId: number
  }
  urls: AppShellProps['urls'] & {
    editZone: string
    zones: string
    newCheckIn: string
    checkIns: string
    newCheckOut: string
    checkOuts: string
  }
  translations: AppShellProps['translations'] & {
    zones: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Show(props: Props) {
  const { zone, urls, translations: t, ...shell } = props

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={zone.name} />

      <div className="mx-auto max-w-3xl space-y-6 py-8 px-4">
        {/* Header card */}
        <div className="overflow-hidden rounded-lg border border-[#E8E8E5]">
          <div className="flex items-center justify-between bg-[#2383E2] px-6 py-4">
            <h1 className="text-xl font-semibold text-white">{zone.name}</h1>
            <span className="rounded bg-white/20 px-3 py-1 text-sm font-medium text-white">
              {zone.eventName}
            </span>
          </div>

          <div className="space-y-6 p-6">
            {/* Details */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-sm font-medium text-[#91918E]">Event</div>
                <div className="mt-1 font-medium text-[#37352F]">{zone.eventName}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-[#91918E]">Name</div>
                <div className="mt-1 font-medium text-[#37352F]">{zone.name}</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="mb-3 text-sm font-semibold text-[#37352F]">Quick Actions</h2>
              <div className="flex flex-wrap gap-2">
                <Button
                  className="bg-[#2383E2] hover:bg-[#1b6ec2]"
                  onClick={() => router.visit(urls.newCheckIn)}
                >
                  <LogIn className="mr-2 h-4 w-4" /> New Check IN
                </Button>
                <Button
                  variant="outline"
                  className="border-[#2383E2] text-[#2383E2]"
                  onClick={() => router.visit(urls.checkIns)}
                >
                  <List className="mr-2 h-4 w-4" /> View Check INs
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => router.visit(urls.newCheckOut)}
                >
                  <LogOut className="mr-2 h-4 w-4" /> New Check OUT
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-600 text-amber-600"
                  onClick={() => router.visit(urls.checkOuts)}
                >
                  <List className="mr-2 h-4 w-4" /> View Check OUTs
                </Button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-[#E8E8E5] bg-[#F1F1EF] px-6 py-4">
            <Button
              variant="outline"
              className="border-[#E8E8E5] text-[#37352F]"
              onClick={() => router.visit(urls.editZone)}
            >
              <Edit className="mr-2 h-4 w-4" /> Edit
            </Button>
            <Button
              variant="outline"
              className="border-[#E8E8E5] text-[#37352F]"
              onClick={() => router.visit(urls.zones)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Zones
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
